import { useEffect, useRef, useState } from "react";

import API from "../utils/API";
import DistrictInfo, { Demographics, Economy, Registration } from "./DistrictInfo";
import DistrictHouseMap from "./DistrictHouseMap";
import "./districtinfo.css";

// A district dropdown that opens the District Info panel for the chosen one. With none chosen it shows the
// whole house on one map (click a district to open it) and the statewide demographics.
export default function DistrictInfoBrowser({ prefix, count }) {
    const [selected, setSelected] = useState("");
    const [overview, setOverview] = useState(null);
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef(null);
    const houseName = prefix === "SD" ? "Senate" : "Assembly";
    const options = [
        { value: "", label: `All ${houseName} districts` },
        ...Array.from({ length: count }, (_, i) => ({
            value: `${prefix}${String(i + 1).padStart(2, "0")}`,
            label: `${houseName} District ${i + 1}`,
        })),
    ];
    const selectedLabel = options.find(o => o.value === selected)?.label;

    // Close on an outside click; open with the chosen district scrolled into view.
    useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        dropdownRef.current?.querySelector(".district-browser-option.selected")?.scrollIntoView({ block: "nearest" });
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [open]);

    const choose = (value) => {
        setSelected(value);
        setOpen(false);
        dropdownRef.current?.querySelector("button")?.focus();
    };

    const onListKeyDown = (e) => {
        const items = [...dropdownRef.current.querySelectorAll(".district-browser-option")];
        const index = items.indexOf(document.activeElement);
        if (e.key === "ArrowDown") { e.preventDefault(); items[Math.min(index + 1, items.length - 1)]?.focus(); }
        else if (e.key === "ArrowUp") { e.preventDefault(); items[Math.max(index - 1, 0)]?.focus(); }
        else if (e.key === "Escape") { setOpen(false); dropdownRef.current.querySelector("button").focus(); }
    };

    useEffect(() => {
        let cancelled = false;
        setOverview(null);
        API.getHouseDistricts(prefix)
            .then(res => { if (!cancelled) setOverview(res.data); })
            .catch(() => { if (!cancelled) setOverview({ features: [], demographics: null }); });
        return () => { cancelled = true; };
    }, [prefix]);

    return (
        <div className="district-browser">
            {/* A custom list rather than a native <select>, so the open list can be capped in height. */}
            <div className="district-browser-dropdown" ref={dropdownRef}>
                <button
                    type="button"
                    className="district-browser-select"
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    aria-label={`${houseName} district`}
                    onClick={() => setOpen(o => !o)}
                    onKeyDown={(e) => {
                        if (e.key === "ArrowDown" && !open) { e.preventDefault(); setOpen(true); }
                    }}
                >
                    <span>{selectedLabel}</span>
                    <i className="bi bi-chevron-down" />
                </button>
                {open && (
                    <ul className="district-browser-list" role="listbox" onKeyDown={onListKeyDown}>
                        {options.map(option => (
                            <li key={option.value || "all"} role="option" aria-selected={option.value === selected}>
                                <button
                                    type="button"
                                    className={`district-browser-option ${option.value === selected ? "selected" : ""}`}
                                    onClick={() => choose(option.value)}
                                >{option.label}</button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {selected && <DistrictInfo key={selected} district={selected} showMember />}

            {!selected && overview && overview.features.length > 0 && (
                <div className="district-info">
                    <h5 className="district-info-title">California {houseName} Districts</h5>
                    <div className="district-info-layout">
                        <div className="district-info-left">
                            {overview.registration && (
                                <div className="district-info-card">
                                    <div className="district-info-card-header">Statewide Voter Registration</div>
                                    <Registration data={overview.registration} />
                                </div>
                            )}
                            {overview.demographics && (
                                <div className="district-info-card">
                                    <div className="district-info-card-header">Statewide Demographics</div>
                                    <Demographics data={overview.demographics} />
                                </div>
                            )}
                            {overview.economy && (
                                <div className="district-info-card">
                                    <div className="district-info-card-header">Statewide Economy &amp; Housing</div>
                                    <Economy data={overview.economy} />
                                </div>
                            )}
                            <p className="district-info-note mb-3">Select a district on the map or from the list above.</p>
                        </div>
                        <div className="district-info-right">
                            <div className="district-info-card district-info-map-card">
                                <DistrictHouseMap features={overview.features} houseName={houseName} onSelect={setSelected} />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
